import { useCallback } from 'react';
import Papa from 'papaparse';
import { copyToClipboard, safeExportValue, safeTsvCell } from '../../utils/resultsTableFormat';

const isExportableDataColumn = (column) => {
  const id = column?.id;
  if (!id || id === 'mrt-row-expand' || id === 'mrt-row-select') return false;
  if (column?.columnDef?.meta?.disableExport) return false;
  return Boolean(column?.columnDef?.accessorKey || column?.columnDef?.accessorFn);
};

export const buildResultsCsvData = (payload, tableColumns = []) => {
  const columns = [];
  const seen = new Set();
  payload.forEach((row) => {
    Object.keys(row || {}).forEach((key) => {
      if (!key || seen.has(key)) return;
      seen.add(key);
      columns.push(key);
    });
  });

  const canonicalColumns = tableColumns.filter(isExportableDataColumn);
  canonicalColumns.forEach((column) => {
    if (seen.has(column.id)) return;
    seen.add(column.id);
    columns.push(column.id);
  });

  const rows = payload.map((row) => {
    const out = {};
    columns.forEach((key) => {
      out[key] = safeExportValue(row ? row[key] : '');
    });
    canonicalColumns.forEach((column) => {
      const { accessorFn, accessorKey } = column.columnDef || {};
      try {
        const value = typeof accessorFn === 'function' ? accessorFn(row) : row?.[accessorKey];
        out[column.id] = safeExportValue(value);
      } catch (_error) {
        // Retain the raw value already copied above if a non-critical display accessor fails.
      }
    });
    return out;
  });

  return { columns, rows };
};

const useResultsTableExport = ({ table, selectedRows, showToast }) => {
  const handleCopySelected = useCallback(
    async (format) => {
      if (!selectedRows.length) return;
      if (format === 'json') {
        await copyToClipboard(JSON.stringify(selectedRows, null, 2));
        showToast(`Copied ${selectedRows.length} row(s) as JSON.`, 'success');
        return;
      }
      const exportableColumns = (table.getAllLeafColumns?.() || []).filter((col) => {
        const id = col?.id;
        if (!id) return false;
        if (id === 'mrt-row-expand' || id === 'mrt-row-select') return false;
        if (!col.getIsVisible?.()) return false;
        return Boolean(col.columnDef?.header);
      });
      const headers = exportableColumns.map((col) => col.columnDef.header);
      const rowsText = selectedRows
        .map((row) =>
          exportableColumns
            .map((col) => {
              const key = col.columnDef?.accessorKey;
              if (key) return safeTsvCell(row[key]);
              if (typeof col.columnDef?.accessorFn === 'function') return safeTsvCell(col.columnDef.accessorFn(row));
              const id = col.id;
              return safeTsvCell(row[id]);
            })
            .join('\t')
        )
        .join('\n');
      await copyToClipboard(`${headers.join('\t')}\n${rowsText}`);
      showToast(`Copied ${selectedRows.length} row(s) as TSV.`, 'success');
    },
    [selectedRows, table, showToast]
  );

  const handleExportCsv = useCallback(async () => {
    const payload = selectedRows.length
      ? selectedRows
      : table.getFilteredRowModel?.().rows?.map((row) => row.original) || [];
    if (typeof window !== 'undefined' && typeof window.__E2E_CAPTURE_EXPORT_PAYLOAD__ === 'function') {
      window.__E2E_CAPTURE_EXPORT_PAYLOAD__(payload);
    }
    if (!payload.length) {
      showToast('Nothing to export.', 'info');
      return;
    }
    const { columns, rows: sanitized } = buildResultsCsvData(payload, table.getAllLeafColumns?.() || []);

    // Quote *all* fields to avoid downstream CSV consumers (Excel/Sheets) getting confused by commas
    // inside values (e.g., brand lists, JSON-serialized columns).
    const csv = Papa.unparse(sanitized, { quotes: true, columns });

    if (typeof window !== 'undefined' && typeof window.__E2E_CAPTURE_CSV__ === 'function') {
      window.__E2E_CAPTURE_CSV__(csv);
      return;
    }

    // Try File System Access API first, fallback to download.
    try {
      const { saveCsvFile } = await import('../../utils/fileSystemAccess');
      const result = await saveCsvFile(csv, `results_export_${new Date().toISOString().split('T')[0]}.csv`);
      if (result.success && !result.cancelled) {
        showToast(`Exported ${payload.length} row(s) to CSV.`, 'success');
        return;
      }
      if (result.cancelled) {
        return; // User cancelled, do not show toast.
      }
    } catch (_err) {
      // File System Access not available, fall through to download.
    }

    // Fallback to download.
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'results_export.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    showToast(`Exported ${payload.length} row(s) to CSV.`, 'success');
  }, [selectedRows, table, showToast]);

  return { handleCopySelected, handleExportCsv };
};

export default useResultsTableExport;
